import { applyOps, invert, applyToPoint, multiply, ROOT_ID, type PathPoint, type Scene } from '@flobi/artwork-core';
import type * as Y from 'yjs';

/** The weight a line is drawn at: the one chosen, or a hairline when none is — 0.5 stays 0.5. */
function lineWeight(weight: number): number {
  return weight > 0 ? weight : 1;
}

/** A point in the world, which is what the canvas hands us. */
export interface Point {
  x: number;
  y: number;
}

/** What the overlay draws while a path is being made. */
export interface PenPreview {
  /** The path's node, so the overlay can put its points in the right place. */
  readonly id: string;
  readonly points: readonly PathPoint[];
  /** Where the next point would go, for the line that follows the pointer. */
  readonly cursor: Point | null;
  /**
   * True while the pointer is near the first anchor, so closing can be shown.
   *
   * The line is drawn to the first anchor rather than to the pointer while this holds:
   * the click is going to land there, so showing it anywhere else is showing a shape
   * that is about to not be the shape you get.
   */
  readonly closing: boolean;
  /** Closing gesture stays editable until pointer release. */
  readonly closingDrag: boolean;
}

/**
 * How near the first anchor the pointer has to be for the path to close, in screen pixels.
 *
 * Generous, because this is the one click in drawing a shape that has to land exactly and
 * the cost of missing it is an almost-closed outline with a hairline gap in it. Within
 * this the line snaps to the anchor and says so, so the reach is something you see rather
 * than something you have to feel for.
 */
const CLOSE_REACH = 14;
/** How far the pointer has to move after a press before it is pulling a curve. */
const DRAG_START = 3;

/**
 * The pen.
 *
 * Click for a corner, drag for a curve: the drag pulls the handle out of the anchor and
 * the other handle mirrors it, which is how every pen since Illustrator's has worked and
 * what a designer's hands already know.
 *
 * The path is a node in the document from the first click, so the shape appears in every
 * editor as it is drawn rather than arriving whole at the end.
 */
export class PenTool {
  private id: string | null = null;
  private points: PathPoint[] = [];
  private cursor: Point | null = null;
  private pressedAt: Point | null = null;
  private dragging = false;
  private closing = false;
  private closingDrag = false;
  /** The node's own origin in world coordinates: points are stored relative to it. */
  private origin: Point = { x: 0, y: 0 };

  constructor(
    private readonly options: {
      page: Y.Doc;
      scene: Scene;
      origin: unknown;
      /** The colour and weight new paths take. */
      style: () => { fill: string; fillOn: boolean; stroke: string; strokeWeight: number; opacity: number };
      /** Turns a hex colour into the engine's. */
      color: (hex: string) => { r: number; g: number; b: number; a: number };
      /** Where the pointer is, in screen pixels, for hit distances. */
      toScreen: (point: Point) => Point;
      /** The artboard a new path goes into: the topmost visible one under the point. */
      artboardAt?: (world: Point) => string | undefined;
      /** Ask for a frame. */
      onChange: () => void;
      /** The path is finished; the canvas selects it and puts the tool back. */
      onFinish: (id: string | null) => void;
      /**
       * Ends the undo step. A path is ONE step however many presses it took, begun on the
       * first press and closed when it is finished or thrown away — so Cmd+Z takes back
       * the path, and never the path together with whatever came before or after it.
       */
      closeStep?: () => void;
    },
  ) {}

  get active(): boolean {
    return this.id !== null && this.options.scene.get(this.id) !== undefined;
  }

  /** The path being drawn, so snapping can be told not to line it up with itself. */
  get pathId(): string | null {
    return this.id;
  }

  preview(): PenPreview | null {
    if (!this.id || !this.options.scene.get(this.id)) return null;
    return { id: this.id, points: this.points, cursor: this.cursor, closing: this.closing, closingDrag: this.closingDrag };
  }

  /**
   * Whether the path being drawn has gone from the page — deleted from the Layers list, by
   * a collaborator, by the toolbar's Undo — and if so, lets it go.
   *
   * The pen held on to its id and went on showing new points that were never saved, and
   * finishing chose a layer that did not exist (QA 2026-09-24, layers-F20).
   */
  private lost(): boolean {
    if (!this.id || this.options.scene.get(this.id)) return false;
    this.reset();
    this.options.closeStep?.();
    this.options.onChange();
    return true;
  }

  /** A press: the next anchor, or a click on the first one to close the path. */
  press(world: Point): void {
    // A path that has gone is not added to: this press starts a new one.
    this.lost();
    if (this.id && this.points.length > 1 && this.nearFirst(world)) {
      this.closingDrag = true;
      this.closing = true;
      this.pressedAt = this.toWorld(this.points[0]!);
      this.dragging = false;
      this.cursor = null;
      this.write();
      return;
    }
    this.pressedAt = world;
    this.dragging = false;

    if (!this.id) {
      this.start(world);
      return;
    }
    const local = this.toLocal(world);
    this.points.push({ x: local.x, y: local.y, kind: 'corner' });
    this.write();
  }

  /** Dragging after a press pulls a curve out of the anchor just placed. */
  drag(world: Point): void {
    if (this.lost() || !this.id || !this.pressedAt) return;
    const from = this.options.toScreen(this.pressedAt);
    const to = this.options.toScreen(world);
    if (!this.dragging && Math.hypot(to.x - from.x, to.y - from.y) < DRAG_START) return;
    this.dragging = true;

    const index = this.closingDrag ? 0 : this.points.length - 1;
    const anchor = this.points[index];
    if (!anchor) return;
    const local = this.toLocal(world);
    // The handle the curve leaves by, and its mirror for the curve arriving: one drag
    // shapes both sides, which is what makes a smooth curve smooth.
    const out: [number, number] = [local.x, local.y];
    const into: [number, number] = [2 * anchor.x - local.x, 2 * anchor.y - local.y];
    this.points[index] = { ...anchor, kind: 'mirrored', out, in: into };
    this.write();
  }

  release(): void {
    if (this.closingDrag) {
      this.finish(true);
      return;
    }
    this.pressedAt = null;
    this.dragging = false;
  }

  /** The pointer moved with nothing pressed: the line that follows it. */
  move(world: Point): void {
    if (this.lost() || !this.id) return;
    this.cursor = world;
    this.closing = this.points.length > 1 && this.nearFirst(world);
    this.options.onChange();
  }

  /** Enter finishes an open path, Escape throws it away, Backspace takes a point back. */
  handleKey(event: KeyboardEvent): boolean {
    if (this.lost() || !this.id) return false;
    if (event.key === 'Enter') {
      event.preventDefault();
      this.finish(false);
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
      return true;
    }
    // Mid-path, Cmd+Z takes the last point back, as Backspace does. Undoing the file
    // instead would pull the path out from under the pen.
    const undoKey = (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'z';
    if (event.key === 'Backspace' || event.key === 'Delete' || undoKey) {
      event.preventDefault();
      this.back();
      return true;
    }
    return false;
  }

  /** Takes the last point back (Backspace); the last one taken throws the path away. */
  back(): void {
    if (this.lost() || !this.id) return;
    this.points.pop();
    if (this.points.length === 0) this.cancel();
    else this.write();
  }

  /** Ends the path, keeping it. Closing joins the last point back to the first. */
  finish(closed: boolean): void {
    // Finishing a path that has gone chooses nothing, rather than a layer that is not there.
    if (this.lost()) {
      this.options.onFinish(null);
      return;
    }
    const id = this.id;
    if (!id) return;
    if (this.points.length < 2 || this.collapsed()) {
      // A single point is not a path, and nor are points all in one place — a double
      // click on empty canvas left an invisible, zero-size 'Path' (QA 2026-09-25, pen-F8).
      this.cancel();
      return;
    }
    this.write(closed, true);
    this.reset();
    this.options.closeStep?.();
    this.options.onFinish(id);
  }

  /**
   * Leaves the path as it stands, because another tool was picked.
   *
   * Picking a tool used to throw a half-drawn path away — and Cmd+Z could not bring it
   * back, because the path and its removal were one undo step. A path of two points or
   * more is kept as an open path, as other editors do; a lone first point is not a path.
   * Nothing is selected and no tool is chosen: the person already chose one.
   */
  keep(): void {
    if (this.lost()) return;
    const id = this.id;
    if (!id) return;
    if (this.points.length < 2 || this.collapsed()) {
      this.reset();
      applyOps(this.options.page, [{ op: 'delete', id }], { scene: this.options.scene, origin: this.options.origin });
      this.options.closeStep?.();
      return;
    }
    this.write(false, true);
    this.reset();
    this.options.closeStep?.();
  }

  /** Abandons the path being drawn, leaving nothing behind. */
  cancel(): void {
    const id = this.id;
    this.reset();
    // Its own step, apart from the drawing: so Cmd+Z after Escape brings the path back
    // rather than undoing something older.
    this.options.closeStep?.();
    if (id) applyOps(this.options.page, [{ op: 'delete', id }], { scene: this.options.scene, origin: this.options.origin });
    this.options.closeStep?.();
    this.options.onFinish(null);
  }

  private start(world: Point): void {
    const style = this.options.style();
    // The canvas's own answer where it has one: the topmost artboard that can be seen. The
    // pen's walk took the BOTTOM of two overlapping boards, and hidden ones too
    // (QA 2026-09-25, pen-F13).
    const parent = this.options.artboardAt ? this.options.artboardAt(world) : this.parentAt(world);
    // Through the inverse of the parent's whole transform, so the first point lands where
    // the click was on a turned or scaled artboard; only its offset used to be taken off
    // (QA 2026-09-25, pen-F16).
    const back = parent ? invert(this.options.scene.worldTransform(parent)) : null;
    const transform = back ? multiply(back, [1, 0, 0, 1, world.x, world.y]) : [1, 0, 0, 1, world.x, world.y];
    this.origin = world;
    this.options.closeStep?.();
    const result = applyOps(
      this.options.page,
      [
        {
          op: 'create',
          ref: 'path',
          parentId: parent ?? ROOT_ID,
          node: {
            type: 'path',
            name: 'Path',
            opacity: style.opacity,
            transform,
            path: { subpaths: [{ closed: false, points: [{ x: 0, y: 0, kind: 'corner' }] }] },
            // While it is being drawn a path is a line, so it can be seen before it is
            // closed; a closed path takes the fill when it is finished.
            fills: [],
            strokes: [{ type: 'solid', color: this.options.color(style.stroke) }],
            strokeStyle: { weight: lineWeight(style.strokeWeight) },
          },
        },
      ],
      { scene: this.options.scene, origin: this.options.origin },
    );
    if (!result.ok) return;
    this.id = result.created.path!;
    this.points = [{ x: 0, y: 0, kind: 'corner' }];
    this.options.onChange();
  }

  /** Writes the points as they stand. On the last write the path also takes its fill. */
  private write(closed = this.closingDrag, last = false): void {
    if (!this.id) return;
    const style = this.options.style();
    const props: Record<string, unknown> = {
      path: { subpaths: [{ closed, points: this.points.map((point) => ({ ...point })) }] },
    };
    if (last && closed) {
      props.fills = style.fillOn ? [{ type: 'solid', color: this.options.color(style.fill) }] : [];
    }
    // The outline is read again at the end too, as the fill is: a colour or weight changed
    // while the path was half drawn used to be ignored (QA 2026-09-25, pen-F15). A closed
    // path with no weight has no outline; an open one keeps a hairline, or it is invisible.
    if (last) {
      if (closed && style.strokeWeight <= 0) props.strokes = [];
      else {
        props.strokes = [{ type: 'solid', color: this.options.color(style.stroke) }];
        props.strokeStyle = { weight: lineWeight(style.strokeWeight) };
      }
    }
    const result = applyOps(this.options.page, [{ op: 'set', id: this.id, props }], {
      scene: this.options.scene,
      origin: this.options.origin,
    });
    // A write the page refused — the path is not there to write to — ends the drawing
    // rather than going on adding points nobody keeps (layers-F20).
    if (!result.ok && !this.options.scene.get(this.id)) {
      this.lost();
      return;
    }
    this.options.onChange();
  }

  private reset(): void {
    this.id = null;
    this.points = [];
    this.cursor = null;
    this.pressedAt = null;
    this.dragging = false;
    this.closing = false;
    this.closingDrag = false;
  }

  /** Whether every anchor sits on the first: something with no size, however many points. */
  private collapsed(): boolean {
    const first = this.points[0];
    return !!first && this.points.every((point) => Math.abs(point.x - first.x) < 1e-6 && Math.abs(point.y - first.y) < 1e-6);
  }

  private nearFirst(world: Point): boolean {
    const first = this.points[0];
    if (!first) return false;
    const a = this.options.toScreen(this.toWorld(first));
    const b = this.options.toScreen(world);
    return Math.hypot(a.x - b.x, a.y - b.y) <= CLOSE_REACH;
  }

  private parentAt(world: Point): string | undefined {
    for (const id of this.options.scene.paintOrder()) {
      const node = this.options.scene.get(id);
      if (node?.type !== 'artboard') continue;
      const inverse = invert(this.options.scene.worldTransform(id));
      if (!inverse) continue;
      const local = applyToPoint(inverse, world.x, world.y);
      if (local.x >= 0 && local.y >= 0 && local.x <= node.width && local.y <= node.height) return id;
    }
    return undefined;
  }

  private toLocal(world: Point): Point {
    const node = this.id ? this.options.scene.get(this.id) : null;
    if (!node) return { x: world.x - this.origin.x, y: world.y - this.origin.y };
    const inverse = invert(this.options.scene.worldTransform(this.id!));
    return inverse ? applyToPoint(inverse, world.x, world.y) : { x: world.x - this.origin.x, y: world.y - this.origin.y };
  }

  private toWorld(point: Point): Point {
    if (!this.id) return { x: point.x + this.origin.x, y: point.y + this.origin.y };
    return applyToPoint(this.options.scene.worldTransform(this.id), point.x, point.y);
  }
}
